/**
 * מעקב תיקונים בביקור הבא (שלב 6ב, D25; D28) — כללים טהורים, משותפים למסך ולדוחות.
 *
 * - ארבעה מצבים (6ב-1): תוקן ואומת · לא תוקן · דווח כתוקן, טרם אומת · לא נבדק בביקור זה.
 *   **״דווח כתוקן״ ≠ ״אומת״** (C-10, RQ-E03).
 * - כל שאר השדות רשות — גם תמונת ״אחרי״ (6ב-2).
 * - **פריט פתוח** (A-117 מורחב ב־D28, A-128) — ממצא שלא נמחק (D20) מביקור קודם שהוגש, **או**
 *   סעיף שסומן ״לא תקין״ / ״דורש תיקון״ בביקור קודם שהוגש, היה בתחולה (D21) ואין עליו ממצא חי
 *   באותו ביקור — ובשני המקרים, שלא אומת כ״תוקן״ בביקור קודם אחר שהוגש. אותו כלל בשרת
 *   (`private.followup_open_items`). פריט פתוח שלא נרשם לו דבר — ״לא נבדק בביקור זה״.
 * - כותרת הפרק סופרת מצבים, ולעולם אינה ״כל הליקויים נסגרו״ אלא אם **כולם** אומתו (RQ-I14).
 * - הפריט מוצג **בנוסח הסעיף** (פרק, מספר קריא, נוסח, הסימון המקורי) — לא בקוד (D28 ס׳ 4).
 * - **D32:** פריט שנכלל במסמך תיקונים **מאושר** (דוח תיקונים, או דוח ביקור עם התיקונים) של
 *   ביקור W נסגר לכל ביקור מאוחר מ־W — בכל מצב שנרשם לו (`followup_closure`). ליקוי שעדיין קיים
 *   נרשם מחדש בצ׳קליסט של הביקור הנוכחי (`currentVisitMark`, `remarkState`).
 */

import type { AnswerScheme } from './checklistContent.ts';
import { formatDate } from './format.ts';
import {
  answerLabel,
  isDefect,
  normalizeSeverity,
  SCHEME_OPTIONS,
  type AnswerValue,
  type NotCheckedReason,
  type Severity,
  type StoredSeverity,
} from './visitEngine.ts';

export type FollowupState =
  'verified_fixed' | 'verified_not_fixed' | 'reported_fixed_pending' | 'not_checked';

export const FOLLOWUP_STATES: FollowupState[] = [
  'verified_fixed',
  'verified_not_fixed',
  'reported_fixed_pending',
  'not_checked',
];

/** תווית קצרה לכפתורים ולטבלה. */
export const FOLLOWUP_STATE_SHORT: Record<FollowupState, string> = {
  verified_fixed: 'תוקן',
  verified_not_fixed: 'לא תוקן',
  reported_fixed_pending: 'דווח כתוקן',
  not_checked: 'לא נבדק',
};

/** תווית מלאה לדוח — מבחינה בין ״אומת״ ל״דווח״. */
export const FOLLOWUP_STATE_LABEL: Record<FollowupState, string> = {
  verified_fixed: 'תוקן ואומת על ידי הבקר',
  verified_not_fixed: 'לא תוקן',
  reported_fixed_pending: 'דווח כתוקן — טרם אומת',
  not_checked: 'לא נבדק בביקור זה',
};

/** מקור הראיה לדיווח התיקון (6ב-2) — שונה מ־`EvidenceSource` של תמונה (camera/upload). */
export type FollowupEvidenceSource =
  'inspector_observed' | 'site_photo' | 'document' | 'verbal' | 'none';

export const FOLLOWUP_EVIDENCE_SOURCES: FollowupEvidenceSource[] = [
  'inspector_observed',
  'site_photo',
  'document',
  'verbal',
  'none',
];

export const FOLLOWUP_EVIDENCE_SOURCE_LABEL: Record<FollowupEvidenceSource, string> = {
  inspector_observed: 'הבקר ראה בעצמו',
  site_photo: 'תמונה מהאתר',
  document: 'מסמך',
  verbal: 'דיווח בעל פה',
  none: 'ללא ראיה',
};

/** שדות הרשומה שהבקר עורך — כמו בשרת (`finding_followup`). */
export interface FollowupFields {
  state: FollowupState;
  not_checked_reason: NotCheckedReason | null;
  fix_reported_by_text: string;
  fix_reported_on: string | null;
  fix_report_text: string;
  evidence_source: FollowupEvidenceSource;
  note: string;
}

export const EMPTY_FOLLOWUP_FIELDS: Omit<FollowupFields, 'state'> = {
  not_checked_reason: null,
  fix_reported_by_text: '',
  fix_reported_on: null,
  fix_report_text: '',
  evidence_source: 'none',
  note: '',
};

/** האם המצב מקבל דיווח תיקון (מי, מתי, איך, מקור הראיה) ותמונת ״אחרי״. */
export function followupAcceptsFixReport(state: FollowupState | null): boolean {
  return state === 'verified_fixed' || state === 'reported_fixed_pending';
}

// --- פריטים פתוחים (D28) ------------------------------------------------------------

/** פריט במעקב: ממצא, או סעיף ״לא תקין״ בלי ממצא מביקור קודם (D28 ס׳ 5). */
export type FollowupItemRef =
  | { kind: 'finding'; findingId: string }
  | { kind: 'item'; sourceVisitId: string; questionId: string };

/** מפתח יציב לפריט — למסך, לתור ולעוגנים בדוח. */
export function followupItemKey(ref: FollowupItemRef): string {
  return ref.kind === 'finding' ? `f:${ref.findingId}` : `q:${ref.sourceVisitId}:${ref.questionId}`;
}

/** הפריט של רשומת מעקב (שרת: `finding_followup`). */
export function followupRecordRef(row: {
  finding_id: string | null;
  source_visit_id?: string | null;
  question_id?: string | null;
}): FollowupItemRef | null {
  if (row.finding_id !== null) return { kind: 'finding', findingId: row.finding_id };
  if (row.source_visit_id == null || row.question_id == null) return null;
  return { kind: 'item', sourceVisitId: row.source_visit_id, questionId: row.question_id };
}

export interface OpenItemsInput {
  /** כל הביקורים באתר (כולל הנוכחי). */
  visits: ReadonlyArray<{
    id: string;
    visitDate: string;
    openedAt: string;
    status: 'draft' | 'submitted' | 'reviewed' | 'approved';
  }>;
  /** ממצאי האתר מביקורים אחרים (גם כאלה שעל סעיף — כדי לדעת שיש לסעיף ממצא חי). */
  findings: ReadonlyArray<{
    id: string;
    visitId: string;
    questionId: string | null;
    withdrawn: boolean;
  }>;
  /** תשובות מביקורים אחרים (לפחות not_ok / needs_fix). */
  answers: ReadonlyArray<{ visitId: string; questionId: string; state: AnswerValue | null }>;
  /** תחולת הסעיפים בביקורים האחרים (visit_item). סעיף בלי שורה — אינו בתחולה. */
  items: ReadonlyArray<{ visitId: string; questionId: string; applicable: boolean }>;
  /** רשומות מעקב מכל הביקורים באתר. */
  followups: ReadonlyArray<{
    visitId: string;
    findingId: string | null;
    sourceVisitId?: string | null;
    questionId?: string | null;
    state: FollowupState;
  }>;
  /**
   * D32 — הסגירות של האתר (`followup_closure`): פריט שנכלל במסמך תיקונים מאושר של ביקור
   * `closingVisitId`. חסר (עותק ישן במכשיר, שרת ישן) — אין סגירות.
   */
  closures?: ReadonlyArray<FollowupClosure>;
  currentVisitId: string;
}

/** D32 — פריט שנכלל במסמך תיקונים מאושר (שורה ב־`followup_closure`). */
export interface FollowupClosure {
  /** הביקור שבו אושר המסמך — סוגר רק ביקור מאוחר ממנו, בלי תלות במצבו. */
  closingVisitId: string;
  findingId?: string | null;
  sourceVisitId?: string | null;
  questionId?: string | null;
}

export interface OpenFollowupItem {
  ref: FollowupItemRef;
  key: string;
  /** הביקור שבו הפריט נרשם (לממצא — ביקור הממצא). */
  sourceVisitId: string;
  /** הסעיף (לממצא כללי — null). */
  questionId: string | null;
}

function earlier(
  a: { visitDate: string; openedAt: string },
  b: { visitDate: string; openedAt: string },
): boolean {
  return a.visitDate < b.visitDate || (a.visitDate === b.visitDate && a.openedAt < b.openedAt);
}

/**
 * הפריטים הפתוחים לביקור הנוכחי — **אותו כלל** כמו `private.followup_open_items` בשרת:
 *   · ממצא: מביקור קודם שהוגש, לא נמחק;
 *   · סעיף: סומן not_ok / needs_fix בביקור קודם שהוגש, בתחולה באותו ביקור, ואין עליו ממצא חי שם;
 *   · ובשניהם — לא אומת כ״תוקן״ (`verified_fixed`) בביקור קודם אחר שהוגש;
 *   · **ו־(D32)** לא נכלל במסמך תיקונים מאושר של ביקור **מוקדם** מהנוכחי — בכל מצב, בלי תלות
 *     במצב הביקור הסוגר (החזרה לטיוטה אינה פותחת). סגירה של ביקור מאוחר — אינה חלה.
 * אימות בביקור הנוכחי (טיוטה) אינו מוציא מהרשימה — הפריט נשאר עם הרשומה שלו. טבלת התרחישים
 * המשותפת לשרת ולמכשיר — `followupScenarios.ts`.
 */
export function openFollowupItems(input: OpenItemsInput): OpenFollowupItem[] {
  const visit = new Map(input.visits.map((v) => [v.id, v]));
  const current = visit.get(input.currentVisitId);
  if (current === undefined) return [];
  const prior = new Set(
    input.visits
      .filter((v) => v.id !== current.id && v.status !== 'draft' && earlier(v, current))
      .map((v) => v.id),
  );
  const closedFindings = new Set<string>();
  const closedItems = new Set<string>();
  for (const ff of input.followups) {
    if (ff.state !== 'verified_fixed' || !prior.has(ff.visitId)) continue;
    if (ff.findingId !== null) closedFindings.add(ff.findingId);
    else if (ff.sourceVisitId != null && ff.questionId != null)
      closedItems.add(`${ff.sourceVisitId}:${ff.questionId}`);
  }
  // D32: הביקור הסוגר לא ידוע במכשיר (עותק חלקי) — אין סגירה; הפריט נשאר (״כלום לא נעלם בשקט״).
  for (const c of input.closures ?? []) {
    const closing = visit.get(c.closingVisitId);
    if (closing === undefined || !earlier(closing, current)) continue;
    if (c.findingId != null) closedFindings.add(c.findingId);
    else if (c.sourceVisitId != null && c.questionId != null)
      closedItems.add(`${c.sourceVisitId}:${c.questionId}`);
  }
  const out: OpenFollowupItem[] = [];
  for (const f of input.findings) {
    if (f.withdrawn || !prior.has(f.visitId) || closedFindings.has(f.id)) continue;
    const ref: FollowupItemRef = { kind: 'finding', findingId: f.id };
    out.push({
      ref,
      key: followupItemKey(ref),
      sourceVisitId: f.visitId,
      questionId: f.questionId,
    });
  }
  const applicable = new Set(
    input.items.filter((i) => i.applicable).map((i) => `${i.visitId}:${i.questionId}`),
  );
  const withFinding = new Set(
    input.findings
      .filter((f) => !f.withdrawn && f.questionId !== null)
      .map((f) => `${f.visitId}:${f.questionId}`),
  );
  for (const a of input.answers) {
    if (a.state !== 'not_ok' && a.state !== 'needs_fix') continue;
    const at = `${a.visitId}:${a.questionId}`;
    if (!prior.has(a.visitId) || !applicable.has(at) || withFinding.has(at) || closedItems.has(at))
      continue;
    const ref: FollowupItemRef = {
      kind: 'item',
      sourceVisitId: a.visitId,
      questionId: a.questionId,
    };
    out.push({
      ref,
      key: followupItemKey(ref),
      sourceVisitId: a.visitId,
      questionId: a.questionId,
    });
  }
  return out;
}

// --- D32: ״בביקור הזה״ — הסעיף המקביל בביקור הנוכחי -----------------------------------

/** תוכן הביקור הנוכחי כפי שהוא במסך (אחרי הפעולות שבתור). */
export interface CurrentVisitContent {
  /** סעיפי גרסת התוכן של הביקור — מזהה, קוד ופרק. */
  questions: ReadonlyArray<{ id: string; code: string; chapterId: string }>;
  /** פרקי הגרסה — לסוג הסימון. */
  chapters: ReadonlyArray<{ id: string; scheme: AnswerScheme }>;
  /** סעיפים שהוסרו מהביקור (D21, `visit_item.applicable = false`). */
  removed: ReadonlySet<string>;
  /** הסימון לפי סעיף (null — לא סומן). */
  answers: ReadonlyMap<string, AnswerValue | null>;
  /** סעיפים שיש עליהם ממצא חי בביקור. */
  findingQuestions: ReadonlySet<string>;
}

/**
 * מה רשום בביקור הנוכחי לסעיף של פריט במעקב (D32, 32-1). ההתאמה **לפי קוד הסעיף** — המזהים
 * שונים בין גרסאות תוכן. ממצא כללי — אין סעיף מקביל; סעיף שאינו בגרסה או שהוסר — ״אינו בביקור״.
 */
export type CurrentVisitMark =
  | { kind: 'general' }
  | { kind: 'absent' }
  | {
      kind: 'present';
      questionId: string;
      scheme: AnswerScheme;
      state: AnswerValue | null;
      hasFinding: boolean;
      /** כבר רשום כליקוי בביקור הזה (״לא תקין״ / ״דורש תיקון״, או ממצא חי). */
      remarked: boolean;
    };

export function currentVisitMark(
  code: string | null,
  current: CurrentVisitContent,
): CurrentVisitMark {
  if (code === null) return { kind: 'general' };
  const q = current.questions.find((x) => x.code === code);
  if (q === undefined || current.removed.has(q.id)) return { kind: 'absent' };
  const scheme = current.chapters.find((c) => c.id === q.chapterId)?.scheme ?? 'standard';
  const state = current.answers.get(q.id) ?? null;
  const hasFinding = current.findingQuestions.has(q.id);
  return {
    kind: 'present',
    questionId: q.id,
    scheme,
    state,
    hasFinding,
    remarked: isDefect(state) || hasFinding,
  };
}

/**
 * הסימון שקובע ״לסמן גם בביקור הזה״: הסימון המקורי (״לא תקין״ / ״דורש תיקון״) כשהוא קיים בסוג
 * הפרק של הביקור הנוכחי; אחרת — הליקוי של אותו סוג.
 */
export function remarkState(original: AnswerValue | null, scheme: AnswerScheme): AnswerValue {
  const options = SCHEME_OPTIONS[scheme];
  if (original !== null && isDefect(original) && options.includes(original)) return original;
  return options.find((o) => isDefect(o)) ?? 'not_ok';
}

/** הכפתור ליד ״לא תוקן״: ״לסמן ״לא תקין״ גם בביקור הזה״ (או ״דורש תיקון״). */
export function remarkLabel(state: AnswerValue, scheme: AnswerScheme): string {
  return `לסמן ״${answerLabel(scheme, state)}״ גם בביקור הזה`;
}

/** השורה ״בביקור הזה: …״ מתחת למתג של פריט שסומן ״לא תוקן״. */
export function currentMarkText(mark: CurrentVisitMark): string {
  if (mark.kind === 'general') return 'ממצא כללי — אם הליקוי עדיין קיים, רשמו ממצא בביקור הזה';
  if (mark.kind === 'absent') return 'הסעיף אינו בביקור הזה — אם הליקוי עדיין קיים, רשמו ממצא כללי';
  if (isDefect(mark.state))
    return `בביקור הזה: ${answerLabel(mark.scheme, mark.state)} — יופיע בכרטיס התיקונים של הביקור הבא`;
  if (mark.hasFinding) return 'בביקור הזה: נרשם ממצא — יופיע בכרטיס התיקונים של הביקור הבא';
  if (mark.state === null) return 'בביקור הזה: לא סומן';
  if (mark.state === 'ok') return 'בביקור הזה: סומן ״תקין״';
  return 'בביקור הזה: לא רלוונטי';
}

/** ספירה למסמך תיקונים ול״לפני הגשה״ — לפי המצב שנרשם ומה שנרשם מחדש בביקור הזה. */
export interface RemarkSummary {
  total: number;
  /** ״לא תוקן״ — ולא נרשמו מחדש בביקור הזה (גם ממצא כללי וסעיף שאינו בביקור). */
  notFixedNotRemarked: number;
  /** ״דווח כתוקן״ — טרם אומתו. */
  reportedPending: number;
  /** ״לא נבדק״, או בלי רשומה. */
  notChecked: number;
}

export function remarkSummary(
  items: ReadonlyArray<{ state: FollowupState | null; mark: CurrentVisitMark }>,
): RemarkSummary {
  const out: RemarkSummary = {
    total: items.length,
    notFixedNotRemarked: 0,
    reportedPending: 0,
    notChecked: 0,
  };
  for (const { state, mark } of items) {
    if (state === 'verified_not_fixed' && !(mark.kind === 'present' && mark.remarked))
      out.notFixedNotRemarked += 1;
    else if (state === 'reported_fixed_pending') out.reportedPending += 1;
    else if (state === null || state === 'not_checked') out.notChecked += 1;
  }
  return out;
}

/** ״לפני הגשה״: פריטים ״לא תוקן״ שלא נרשמו מחדש (D32 — אחרי מסמך תיקונים הם לא יחזרו). */
export function notRemarkedText(n: number): string {
  return n === 1
    ? 'ליקוי אחד מהביקור הקודם סומן ״לא תוקן״ ולא סומן בביקור הזה. אחרי דוח תיקונים הוא לא יופיע בביקור הבא.'
    : `${n} ליקויים מהביקור הקודם סומנו ״לא תוקן״ ולא סומנו בביקור הזה. אחרי דוח תיקונים הם לא יופיעו בביקור הבא.`;
}

/**
 * לפני הסקירה של מסמך תיקונים — מה ייצא מהרשימה של הביקורים הבאים, ומה מזה לא אומת ולא נרשם
 * מחדש. ספירה של אפס אינה מודפסת; כולן אפס — השורה השנייה אינה מופיעה.
 */
export function closureNoteLines(s: RemarkSummary): string[] {
  const first =
    s.total === 1
      ? 'אחרי האישור, הפריט שבמסמך לא יופיע בכרטיס התיקונים של הביקורים הבאים.'
      : `אחרי האישור, ${s.total} הפריטים שבמסמך לא יופיעו בכרטיס התיקונים של הביקורים הבאים.`;
  const parts = [
    s.notFixedNotRemarked > 0 ? `${s.notFixedNotRemarked} לא תוקנו ולא נרשמו מחדש` : null,
    s.reportedPending > 0 ? `${s.reportedPending} דווחו כתוקנים וטרם אומתו` : null,
    s.notChecked > 0 ? `${s.notChecked} לא נבדקו` : null,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? [first] : [first, `מהם: ${parts.join(' · ')}.`];
}

/** אחרי האישור: ״N פריטים נסגרו במסמך הזה.״ */
export function closedInDocumentText(n: number): string {
  return n === 1 ? 'פריט אחד נסגר במסמך הזה.' : `${n} פריטים נסגרו במסמך הזה.`;
}

// --- נתוני הפריט כפי שהשרת שומר בדוחות (D28) ------------------------------------------

/** תמונת ״לפני״ (מה־snapshot של הדוח המקורי) או ״אחרי״ (מהביקור הזה). */
export interface FollowupPhoto {
  id: string;
  storage_path: string;
  mime_type: string;
  caption: string;
  observed_at: string | null;
  observed_at_source: 'device' | 'user_entered' | 'unknown';
  stored: boolean;
}

/** רשומת המעקב של הביקור הזה לפריט — רק מה שהבקר רשם. */
export interface FollowupRecordData {
  state: FollowupState;
  not_checked_reason: NotCheckedReason | null;
  fix_reported_by_text: string;
  fix_reported_on: string | null;
  fix_report_text: string;
  evidence_source: FollowupEvidenceSource;
  note: string;
  recorded_by: string;
  recorded_at: string;
}

/**
 * פריט בדוח התיקונים / בפרק התיקונים — בדיוק מה ש־`private.followup_items_data` בונה.
 * נוסח הסעיף והפרק — מגרסת התוכן של הביקור המקורי; המספר — כפי שהודפס בדוח המקורי.
 */
export interface FollowupItemData {
  kind: 'finding' | 'item';
  finding_id: string | null;
  source_visit_id: string;
  question_id: string | null;
  chapter_title: string | null;
  answer_scheme: AnswerScheme | null;
  item_number: string | null;
  item_title: string | null;
  original_state: AnswerValue | null;
  original_note: string;
  finding_number: number | null;
  severity: StoredSeverity | null;
  description: string | null;
  location_text: string;
  area_name: string | null;
  source: {
    visit_id: string;
    visit_date: string;
    report_number: string;
    report_approved: boolean;
  };
  /** null — לא נרשם דבר בביקור זה (״לא נבדק בביקור זה״). */
  followup: FollowupRecordData | null;
  before: FollowupPhoto[];
  after: FollowupPhoto[];
}

export interface CorrectionsData {
  items: FollowupItemData[];
}

export const GENERAL_FINDING_TEXT = 'ממצא כללי';

/**
 * איך הפריט נקרא — בלי הקוד הפנימי (D28 ס׳ 4): מספר קריא ונוסח הסעיף, או ״ממצא כללי״.
 */
export function followupItemTitle(item: {
  item_number: string | null;
  item_title: string | null;
}): string {
  const title = item.item_title?.trim() ?? '';
  if (title === '') return GENERAL_FINDING_TEXT;
  return item.item_number === null ? title : `${item.item_number} ${title}`;
}

/** הסימון המקורי (״לא תקין״ / ״דורש תיקון״ …); null — לא סומן, ואין מה להדפיס. */
export function originalMarkLabel(item: {
  answer_scheme: AnswerScheme | null;
  original_state: AnswerValue | null;
}): string | null {
  if (item.original_state === null) return null;
  return answerLabel(item.answer_scheme ?? 'standard', item.original_state);
}

/** מאיזה דוח הפריט: ״מדוח הביקור מיום 20.09.2026״ (המספר הטכני — בנפרד, במשני). */
export function followupSourceText(visitDate: string): string {
  return `מדוח הביקור מיום ${formatDate(visitDate)}`;
}

// --- ספירה וכותרת (RQ-I14) --------------------------------------------------------

export interface FollowupCounts {
  verifiedFixed: number;
  notFixed: number;
  reportedPending: number;
  notChecked: number;
  total: number;
}

/** סופר מצבים; ממצא פתוח בלי רשומה = ״לא נבדק בביקור זה״. */
export function countFollowups(states: ReadonlyArray<FollowupState | null>): FollowupCounts {
  const counts: FollowupCounts = {
    verifiedFixed: 0,
    notFixed: 0,
    reportedPending: 0,
    notChecked: 0,
    total: states.length,
  };
  for (const s of states) {
    if (s === 'verified_fixed') counts.verifiedFixed += 1;
    else if (s === 'verified_not_fixed') counts.notFixed += 1;
    else if (s === 'reported_fixed_pending') counts.reportedPending += 1;
    else counts.notChecked += 1;
  }
  return counts;
}

export const ALL_CLOSED_TEXT = 'כל הליקויים מהביקורים הקודמים תוקנו ואומתו';

/**
 * כותרת פרק המעקב: ספירה לפי מצב. ״כל הליקויים … תוקנו ואומתו״ — **רק** כשיש ממצאים
 * וכולם `verified_fixed`. ״דווח כתוקן״ אינו נספר כסגור (RQ-I14, C-10).
 */
export function followupHeadline(counts: FollowupCounts): string {
  if (counts.total === 0) return 'אין ממצאים פתוחים מביקורים קודמים';
  if (counts.verifiedFixed === counts.total) return `${ALL_CLOSED_TEXT} (${counts.total})`;
  return [
    `תוקנו ואומתו ${counts.verifiedFixed}`,
    `לא תוקנו ${counts.notFixed}`,
    `דווחו כתוקנים, טרם אומתו ${counts.reportedPending}`,
    `לא נבדקו ${counts.notChecked}`,
  ].join(' · ');
}

/** ״מעקב: 3/5״ לפס ההתקדמות — כמה ממצאים פתוחים קיבלו רשומה (לא ״לא נבדק״ מרומז). */
export function followupProgress(counts: FollowupCounts, recorded: number): string {
  return `מעקב: ${recorded}/${counts.total}`;
}

// --- מודל פרק התיקונים / דוח התיקונים (D28) -------------------------------------------

export interface CorrectionsRow {
  /** עוגן יציב בדוח (קישור מהטבלה לכרטיס). */
  anchor: string;
  item: FollowupItemData;
  /** null — לא נרשם דבר בביקור זה. */
  state: FollowupState | null;
  /** ״5.2 פתחים ברצפה״ / ״ממצא כללי״ — בלי קוד (D28 ס׳ 4). */
  title: string;
  /** הסימון המקורי (״לא תקין״ / ״דורש תיקון״); null — לא סומן. */
  mark: string | null;
  severity: Severity | null;
}

/** הפריטים לפי הדוח המקורי שבו נרשמו. */
export interface CorrectionsGroup {
  sourceVisitId: string;
  sourceDate: string;
  reportNumber: string;
  reportApproved: boolean;
  rows: CorrectionsRow[];
}

export interface CorrectionsModel {
  rows: CorrectionsRow[];
  groups: CorrectionsGroup[];
  counts: FollowupCounts;
  /** לעולם לא ״הכול נסגר״ אלא אם כולם אומתו (RQ-I14). */
  headline: string;
}

/**
 * הפריטים כפי שהשרת סידר אותם (לפי הדוח המקורי ולפי סדר הצ׳קליסט), מקובצים לפי הדוח המקורי.
 * פריט בלי רשומה נספר ״לא נבדק בביקור זה״ — אינו נעלם.
 */
export function buildCorrectionsModel(data: CorrectionsData | null | undefined): CorrectionsModel {
  const items = Array.isArray(data?.items) ? data.items : [];
  const rows: CorrectionsRow[] = items.map((item) => ({
    anchor:
      item.finding_id !== null
        ? `corr-f-${item.finding_id}`
        : `corr-q-${item.source_visit_id}-${item.question_id ?? 'x'}`,
    item,
    state: item.followup?.state ?? null,
    title: followupItemTitle(item),
    mark: originalMarkLabel(item),
    severity: item.kind === 'finding' ? normalizeSeverity(item.severity) : null,
  }));
  const groups: CorrectionsGroup[] = [];
  for (const row of rows) {
    const last = groups.at(-1);
    if (last !== undefined && last.sourceVisitId === row.item.source_visit_id) last.rows.push(row);
    else
      groups.push({
        sourceVisitId: row.item.source_visit_id,
        sourceDate: row.item.source.visit_date,
        reportNumber: row.item.source.report_number,
        reportApproved: row.item.source.report_approved,
        rows: [row],
      });
  }
  const counts = countFollowups(rows.map((r) => r.state));
  return { rows, groups, counts, headline: followupHeadline(counts) };
}

// --- מספר דוח קודם ---------------------------------------------------------------

/**
 * מספר דוח הביקור כפי שהשרת קובע (`private.report_number`, A-77): `V-{YYYYMMDD}-{6 תווים}`.
 * למסך בלבד (״מדוח V-… מ־…״); בנתוני הדוח המספר מגיע מהשרת.
 */
export function visitReportNumber(visitDate: string, visitId: string): string {
  return `V-${visitDate.slice(0, 10).replace(/-/g, '')}-${visitId.replace(/-/g, '').slice(0, 6).toUpperCase()}`;
}
